/**
 * channelTypeMetadata — centraliza os textos e ícones de UI por `providerId`.
 * Usado pelo ChannelCard para adaptar:
 *  - Label do bloco de identificador ("Número conectado" / "Perfil Instagram" / etc)
 *  - Nome curto no rodapé quando não há device (ex: "Meta Business", "Email nativo")
 *  - Ícone lucide (react-icons/fi) associado ao tipo
 *  - Cor do provider badge no canto do avatar
 */

import { ProviderId } from '@troia-v3/types';

export interface ChannelTypeMetadata {
  /** Label do bloco de identificador (ex: "Número conectado") */
  identifierLabel: string;
  /** Ícone react-icons/fi usado no bloco de identificador */
  identifierIcon: 'phone' | 'camera' | 'facebook' | 'mail' | 'send' | 'globe' | 'message-circle' | 'link';
  /** Fallback para o rodapé quando não há accountInfo.platform (ex: "Meta Business") */
  defaultFooter: string;
  /** Cor hex do provider badge (pequeno círculo no canto do avatar) */
  badgeColor: string;
  /** Cor hex para ícone/accent do bloco de identificador */
  accentColor: string;
  /** Label das métricas (alguns canais usam "Emails" em vez de "Msgs") */
  messagesLabel: string;
  conversationsLabel: string;
}

const WHATSAPP: ChannelTypeMetadata = {
  identifierLabel: 'Número conectado',
  identifierIcon: 'phone',
  defaultFooter: 'WhatsApp',
  badgeColor: '#25d366',
  accentColor: '#8b5cf6',
  messagesLabel: 'Msgs/7d',
  conversationsLabel: 'Conversas',
};

const INSTAGRAM: ChannelTypeMetadata = {
  identifierLabel: 'Perfil Instagram',
  identifierIcon: 'camera',
  defaultFooter: 'Meta Business',
  badgeColor: '#dc2743',
  accentColor: '#dc2743',
  messagesLabel: 'Msgs/7d',
  conversationsLabel: 'Conversas',
};

const FACEBOOK: ChannelTypeMetadata = {
  identifierLabel: 'Página Facebook',
  identifierIcon: 'facebook',
  defaultFooter: 'Meta Business',
  badgeColor: '#1877f2',
  accentColor: '#1877f2',
  messagesLabel: 'Msgs/7d',
  conversationsLabel: 'Conversas',
};

const EMAIL: ChannelTypeMetadata = {
  identifierLabel: 'Endereço de email',
  identifierIcon: 'mail',
  defaultFooter: 'Email',
  badgeColor: '#64748b',
  accentColor: '#64748b',
  messagesLabel: 'Emails/7d',
  conversationsLabel: 'Threads',
};

const TELEGRAM: ChannelTypeMetadata = {
  identifierLabel: 'Bot Telegram',
  identifierIcon: 'send',
  defaultFooter: 'Bot API',
  badgeColor: '#26a5e4',
  accentColor: '#26a5e4',
  messagesLabel: 'Msgs/7d',
  conversationsLabel: 'Conversas',
};

const WEBSITE: ChannelTypeMetadata = {
  identifierLabel: 'Site',
  identifierIcon: 'globe',
  defaultFooter: 'Visitantes anônimos',
  badgeColor: '#8b5cf6',
  accentColor: '#8b5cf6',
  messagesLabel: 'Msgs/7d',
  conversationsLabel: 'Conversas',
};

const API_WEBHOOK: ChannelTypeMetadata = {
  identifierLabel: 'Endpoint',
  identifierIcon: 'link',
  defaultFooter: 'API',
  badgeColor: '#475569',
  accentColor: '#475569',
  messagesLabel: 'Eventos/7d',
  conversationsLabel: 'Conexões',
};

const FALLBACK: ChannelTypeMetadata = {
  identifierLabel: 'Identificador',
  identifierIcon: 'message-circle',
  defaultFooter: 'Canal',
  badgeColor: '#8b5cf6',
  accentColor: '#8b5cf6',
  messagesLabel: 'Msgs/7d',
  conversationsLabel: 'Conversas',
};

const METADATA_BY_PROVIDER: Partial<Record<ProviderId, ChannelTypeMetadata>> = {
  [ProviderId.WHATSAPP_BUSINESS]: WHATSAPP,
  [ProviderId.GATEWAY_WHATSAPP]: WHATSAPP,

  [ProviderId.INSTAGRAM_MESSAGING]: INSTAGRAM,

  [ProviderId.FACEBOOK_MESSENGER]: FACEBOOK,

  [ProviderId.TELEGRAM_BOT]: TELEGRAM,

  [ProviderId.EMAIL_SMTP]: EMAIL,
  [ProviderId.EMAIL_SENDGRID]: { ...EMAIL, defaultFooter: 'SendGrid' },
  [ProviderId.EMAIL_SES]: { ...EMAIL, defaultFooter: 'AWS SES' },
  [ProviderId.EMAIL_RESEND]: { ...EMAIL, defaultFooter: 'Email' },
  [ProviderId.GMAIL_API]: { ...EMAIL, defaultFooter: 'Gmail' },

  [ProviderId.WEBSITE_CHAT]: WEBSITE,
  [ProviderId.WEBSITE_WIDGET]: WEBSITE,

  [ProviderId.API_WEBHOOK]: API_WEBHOOK,

  [ProviderId.META]: { ...WHATSAPP, defaultFooter: 'Meta Business' },
};

/**
 * Resolve metadata pro providerId. Retorna fallback neutro para providers sem mapeamento.
 */
export function getChannelTypeMetadata(providerId: string | undefined): ChannelTypeMetadata {
  if (!providerId) return FALLBACK;
  return METADATA_BY_PROVIDER[providerId as ProviderId] ?? FALLBACK;
}

/**
 * Formata o identifier pra exibição baseado no tipo de canal.
 * - Phone (whatsapp): formata dígitos para "+55 47 99123-6370"
 * - Outros: retorna como veio
 */
export function formatChannelIdentifier(providerId: string | undefined, identifier: string | undefined): string {
  if (!identifier) return '';

  const isPhone = providerId === ProviderId.WHATSAPP_BUSINESS
    || providerId === ProviderId.GATEWAY_WHATSAPP
    || providerId === ProviderId.META;

  if (!isPhone) return identifier;

  const digits = identifier.replace(/\D/g, '');
  if (digits.length === 13) {
    return `+${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 9)}-${digits.slice(9)}`;
  }
  if (digits.length === 12) {
    return `+${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 8)}-${digits.slice(8)}`;
  }
  return digits ? `+${digits}` : identifier;
}

/**
 * Normaliza o nome do dispositivo/plataforma pra exibição.
 * ex: "iphone" → "iPhone", "android" → "Android", "web" → "Web".
 */
export function formatPlatform(platform: string | undefined): string | undefined {
  if (!platform) return undefined;
  const lower = platform.toLowerCase();
  if (lower === 'iphone' || lower === 'ios') return 'iPhone';
  if (lower === 'android') return 'Android';
  if (lower === 'web') return 'Web';
  if (lower === 'windows') return 'Windows';
  if (lower === 'mac' || lower === 'macos') return 'macOS';
  // Capitaliza primeira letra como fallback
  return platform.charAt(0).toUpperCase() + platform.slice(1);
}
